import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { MainLayout } from "@/components/layout/MainLayout";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { ProductosTab } from "@/components/menu/ProductosTab";
import { CategoriasTab } from "@/components/menu/CategoriasTab";
import { InsumosTab } from "@/components/menu/InsumosTab";
import { MovimientosTab } from "@/components/menu/MovimientosTab";
import { VariacionesTab } from "@/components/menu/VariacionesTab";
import { CombosTab } from "@/components/menu/CombosTab";
import { BackupsTab } from "@/components/menu/BackupsTab";

export default function MenuPage() {
  const { authAxios } = useAuth();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("productos");
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const fetchData = useCallback(async () => {
    try {
      const [catRes, prodRes] = await Promise.all([authAxiosRef.current.get("/menu/categories"), authAxiosRef.current.get("/menu/products")]);
      setCategories(catRes.data); setProducts(prodRes.data);
    } catch { toast.error(t('errors.generic')); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { key: "productos", label: t('menu.products').toUpperCase() },
    { key: "categorias", label: t('menu.categories').toUpperCase() },
    { key: "insumos", label: "INSUMOS" },
    { key: "movimientos", label: "MOVIMIENTOS" },
    { key: "variaciones", label: t('menu.variations').toUpperCase() },
    { key: "combos", label: "COMBOS" },
    { key: "backups", label: "BACKUPS" },
  ];

  if (loading) return (
    <MainLayout title={t('admin.menuPage')}>
      <div className="flex items-center justify-center py-32" data-testid="menu-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    </MainLayout>
  );

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="menu-page">
        <div className="border-b border-gray-200">
          <div className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button key={tab.key} className={`pb-3 text-xs sm:text-sm font-bold uppercase tracking-wide transition-colors border-b-2 whitespace-nowrap shrink-0 ${activeTab === tab.key ? "text-emerald-700 border-emerald-600" : "text-gray-500 border-transparent hover:text-gray-700"}`} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}>{tab.label}</button>
            ))}
          </div>
        </div>
        {activeTab === "productos" && <ProductosTab products={products} setProducts={setProducts} categories={categories} onRefresh={fetchData} />}
        {activeTab === "categorias" && <CategoriasTab categories={categories} onRefresh={fetchData} />}
        {activeTab === "insumos" && <InsumosTab />}
        {activeTab === "movimientos" && <MovimientosTab />}
        {activeTab === "variaciones" && <VariacionesTab products={products} />}
        {activeTab === "combos" && <CombosTab products={products} />}
        {activeTab === "backups" && <BackupsTab onRefresh={fetchData} />}
      </div>
    </MainLayout>
  );
}
